import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { FaTimes, FaSave, FaSpinner, FaUser } from 'react-icons/fa';
import { ProjectAsset, updateProjectAsset } from '../../api/assets';
import { getProjectUsers } from '../../api/projects';
import { UserSearch } from '../UserSearch';

interface EditAssetModalProps {
  asset: ProjectAsset;
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onAssetUpdated?: () => void;
}

export const EditAssetModal: React.FC<EditAssetModalProps> = ({
  asset,
  projectId,
  isOpen,
  onClose,
  onAssetUpdated
}) => {
  const { keycloak } = useKeycloak();
  const [formData, setFormData] = useState({
    name: asset.name,
    description: asset.description || '',
    quantity: asset.quantity,
    current_responsible_person_id: asset.current_responsible_person_id || '',
    notes: asset.notes || ''
  });
  const [projectUsers, setProjectUsers] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  // Load project users
  const loadProjectUsers = async () => {
    if (!keycloak.token || !projectId) return;
    
    try {
      const usersResponse = await getProjectUsers(keycloak.token, projectId);
      const users = usersResponse.results || [];
      setProjectUsers(users.map(u => u.user).filter(Boolean));
      console.log('👥 Project users loaded for edit modal:', users.length, 'users');
    } catch (error) {
      console.error('Error loading project users:', error);
      toast.error('Failed to load project users');
    }
  };

  // Initialize form when asset changes
  useEffect(() => {
    setFormData({
      name: asset.name,
      description: asset.description || '',
      quantity: asset.quantity,
      current_responsible_person_id: asset.current_responsible_person_id || '',
      notes: asset.notes || ''
    });
  }, [asset]);

  // Load users when modal opens
  useEffect(() => {
    if (isOpen) {
      loadProjectUsers();
    }
  }, [isOpen]);

  // Handle save
  const handleSave = async () => {
    if (!keycloak.token || !projectId) return;
    
    setSaving(true);
    try {
      const updateData = {
        id: asset.id,
        project_id: projectId,
        item: asset.item,
        name: formData.name,
        description: formData.description,
        quantity: formData.quantity,
        ordered_at: asset.ordered_at,
        eta: asset.eta,
        tracking_number: asset.tracking_number,
        lead_time: asset.lead_time,
        esd: asset.esd,
        isd: asset.isd,
        unit_id: asset.unit_id,
        current_status_id: asset.current_status_id,
        current_responsible_person_id: formData.current_responsible_person_id || null,
        is_installed: asset.is_installed,
        notes: formData.notes
      };

      await updateProjectAsset(keycloak.token, projectId, asset.id, updateData, keycloak);
      
      toast.success('Asset updated successfully');
      onClose();
      
      // Notify parent to refresh data
      if (onAssetUpdated) {
        onAssetUpdated();
      }
    } catch (error) {
      console.error('Error updating asset:', error);
      toast.error('Failed to update asset');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 font-['Arcadia_Display',system-ui,sans-serif]">
            Edit Asset: {asset.name}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter asset name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter description"
                />
              </div>
            </div>

            {/* Assignment */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                <FaUser className="w-4 h-4" />
                <span>Assignment</span>
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsible Person
                </label>
                <UserSearch
                  defaultValue={formData.current_responsible_person_id ? [formData.current_responsible_person_id] : []}
                  defaultOptions={projectUsers}
                  onChange={(user) => setFormData(prev => ({ 
                    ...prev, 
                    current_responsible_person_id: user ? user.id : '' 
                  }))}
                  iconOnly={false}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Select a user to assign responsibility for this asset
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Notes</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter any additional notes about this asset"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !formData.name.trim()}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}; 